import { ToggleButtonGroup, ToggleButton } from '@/components/ToggleButtonGroup'
import { SelectField } from '@/components/SelectField'
import { CommunityBackButton } from '@/components/CommunityBackButton'
import { getLabels, getSortOptions } from '@/data/config'
import type { CardType } from '@/utils/urlState'

interface ListControlsProps {
	cardType: CardType
	onCardTypeChange: (type: CardType) => void
	tabs?: CardType[]
	unitCount: number
	planCount: number
	propertyCount?: number
	propertyTabLabel?: string
	sort?: string
	onSortChange?: (sort: string) => void
	hideSort?: boolean
	/** Replaces the tabs with a back button (portfolio community view). */
	onBack?: () => void
	backLabel?: string
	className?: string
}

export function ListControls ({
	cardType,
	onCardTypeChange,
	tabs = ['units', 'plans'],
	unitCount,
	planCount,
	propertyCount = 0,
	propertyTabLabel = 'Properties',
	sort = 'pr_asc',
	onSortChange,
	hideSort = false,
	onBack,
	backLabel,
	className = ''
}: ListControlsProps) {
	const { unitTabLabel, planTabLabel } = getLabels()
	const tabLabels: Record<CardType, string> = {
		units: `${unitCount} ${unitTabLabel}`,
		plans: `${planCount} ${planTabLabel}`,
		properties: `${propertyCount} ${propertyTabLabel}`
	}
	return (
		<div
			className={`spaces-list-controls spaces:flex spaces:items-center spaces:bg-canvas-base spaces:border-b spaces:border-canvas-3 spaces:pl-8 spaces:pr-12 spaces:py-8 ${className}`}
		>
			{onBack ? (
				<CommunityBackButton onBack={onBack} label={backLabel} />
			) : tabs.length > 1 ? (
				/* Card type toggle — full width on mobile */
				<ToggleButtonGroup
					type='single'
					value={cardType}
					onValueChange={v => onCardTypeChange(v as CardType)}
					size='md'
					className='spaces:w-full spaces:md:w-auto'
				>
					{tabs.map(tab => (
						<ToggleButton
							key={tab}
							value={tab}
							label={tabLabels[tab]}
							className='spaces:flex-1 spaces:md:flex-none'
						/>
					))}
				</ToggleButtonGroup>
			) : (
				// Single visible tab: no switcher, but keep the count context for the active list.
				<span className='spaces-object-label spaces:px-8 spaces:py-13 spaces:text-16/24 spaces:font-semi spaces:text-content-1'>
					{tabLabels[cardType]}
				</span>
			)}

			<div className='spaces:hidden spaces:md:block spaces:flex-1' />

			{/* Sort — desktop only; invisible (not removed) when hidden so the bar height doesn't shift */}
			<div className={`spaces-list-sort spaces:hidden spaces:md:flex spaces:items-center${hideSort ? ' spaces:invisible' : ''}`} aria-hidden={hideSort || undefined}>
				<SelectField
					options={getSortOptions()}
					value={sort}
					onValueChange={v => onSortChange?.(v)}
					label='Sort by'
					align='end'
				/>
			</div>
		</div>
	)
}
